import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Target, Wallet, Repeat, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BrandLogo } from '@/components/BrandLogo';
import { LanguageToggle } from '@/components/LanguageToggle';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LanguageContext';
import { TEST_IDS } from '@/constants/testIds/app';

export default function Onboarding() {
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const { t } = useLang();
  const [demoLoading, setDemoLoading] = useState(false);

  const startDemo = async () => {
    setDemoLoading(true);
    try {
      const res = await api.post('/auth/demo');
      setAuth(res.data.user);
      toast.success(t('onboarding.demoWelcome'));
      navigate('/dashboard');
    } catch (e) {
      toast.error(t('onboarding.demoError'));
    } finally {
      setDemoLoading(false);
    }
  };

  // Each pillar gets its own accent from the brand family (cyan / blue / violet),
  // mirroring the three stacked layers of the canonical icon.
  const chips = [
    { icon: Target, label: t('onboarding.chipGoals'), tone: 'text-brand-cyan' },
    { icon: Wallet, label: t('onboarding.chipMoney'), tone: 'text-brand-blue' },
    { icon: Repeat, label: t('onboarding.chipHabits'), tone: 'text-brand-violet' },
  ];

  return (
    <div className="app-canvas relative flex min-h-screen flex-col">
      <div className="app-shell flex min-h-screen flex-col px-6 pb-10 pt-16">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-1 flex-col"
        >
          <div className="flex items-center justify-between gap-3">
            <BrandLogo size="lg" withText />
            <LanguageToggle />
          </div>

          <div className="mt-auto">
            <h1 className="font-display text-4xl font-semibold leading-[1.1] tracking-tight text-foreground">
              {t('onboarding.title')}
            </h1>
            <p className="mt-4 text-base leading-7 text-foreground/65">
              {t('onboarding.subtitle')}
            </p>

            <div className="mt-6 flex flex-wrap gap-2">
              {chips.map(({ icon: Icon, label, tone }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-1.5 rounded-full border border-brand-blue/20 bg-white/[0.06] px-3 py-1.5 text-xs font-medium text-foreground/80"
                >
                  <Icon className={`h-3.5 w-3.5 ${tone}`} />
                  {label}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-10 space-y-3">
            <Button
              data-testid={TEST_IDS.onboarding.getStarted}
              onClick={() => navigate('/auth')}
              className="btn-brand h-12 w-full rounded-xl text-base font-semibold text-brand-ink"
            >
              {t('onboarding.getStarted')}
              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
            <Button
              data-testid={TEST_IDS.onboarding.demo}
              onClick={startDemo}
              disabled={demoLoading}
              variant="secondary"
              className="h-12 w-full rounded-xl border border-brand-blue/25 bg-white/[0.06] text-base font-medium text-foreground hover:border-brand-cyan/40 hover:bg-white/[0.1]"
            >
              {demoLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t('onboarding.demo')}
            </Button>
            <p className="pt-1 text-center text-xs text-foreground/45">
              {t('onboarding.tagline')}
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
